// Admin API client for AI model currency checks and judge calibration (ZON-266).
// Mirrors the req() helper in adminProgramNews.ts; ApiError is shared so callers can branch on
// status (403 read-only, 409 not promotable, 404 gone).

import { ApiError } from "./adminProgramNews";

const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

async function req<T>(
  method: string,
  path: string,
  body?: Record<string, unknown>,
  signal?: AbortSignal
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body) headers["Content-Type"] = "application/json";

  try {
    const token = sessionStorage.getItem("recruit_ai_token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
  } catch {
    // Ignore token retrieval errors
  }

  const resp = await fetch(withBase(path), {
    method,
    headers: Object.keys(headers).length ? headers : undefined,
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  const text = await resp.text();
  let data: { error?: string; message?: string; raw?: string; [key: string]: unknown };
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!resp.ok) {
    const msg = data?.error || data?.message || text || `Request failed (${resp.status})`;
    throw new ApiError(msg, resp.status, data);
  }
  return data as T;
}

// ---- Types (the API contract) ----

export type EffectiveSource = "assignment" | "env" | "default";
export type CandidateKind = "newer" | "replacement";
export type CandidateStatus =
  | "detected"
  | "evaluating"
  | "passed"
  | "failed"
  | "dismissed"
  | "promoted"
  /** Older shortlist entry replaced by a newer model; informational only. */
  | "superseded";

export type ProviderId = "openai" | "anthropic";

export interface EvalCheck {
  name: string;
  passed: boolean;
  value: number | string | null;
  limit: number | string | null;
}

export interface EvalResults {
  kind: string;
  passed: boolean;
  ranAt: string | null;
  candidateModel: string;
  incumbentModel: string;
  /** Plain text from older runs; the real API now sends a structured object (never render it directly). */
  summary: string | Record<string, unknown> | null;
  checks: EvalCheck[];
  notes: string | null;
}

/** List price in USD per 1M tokens. */
export interface ModelPrice {
  input: number;
  output: number;
}

export interface PriceTable {
  asOf: string;
  unit: string;
  /** "fetched" = pulled from the vendors' pricing pages; "built-in" = our hand-kept table. */
  source?: "fetched" | "built-in";
  fetchedAt?: string | null;
}

export interface ModelCandidate {
  id: string;
  model: string;
  currentModel: string;
  kind: CandidateKind;
  status: CandidateStatus;
  recommendation: string | null;
  detectedAt: string | null;
  /** True when the API server can run this candidate's test itself (QA only). */
  canRunHere: boolean;
  /** Plain-language hint for where to run the test when canRunHere is false. */
  runHint: string | null;
  /** ISO time the current test started; set while status is "evaluating". */
  evalStartedAt: string | null;
  evalResults: EvalResults | null;
  /** Optional: older API responses omit it. Treat missing as null. */
  price?: ModelPrice | null;
  /** Candidate average price / current average price; null when either is unknown. */
  priceVsCurrent?: number | null;
  /** Which company makes the model. Optional: older API responses omit it. */
  provider?: ProviderId | null;
}

export interface ModelAssignment {
  model: string;
  previousModel: string | null;
  assignedAt: string | null;
  assignedBy: string | null;
  needsRebaseline: boolean;
}

export interface UseCaseOverview {
  id: string;
  label: string;
  hasEvalHarness: boolean;
  effective: {
    model: string;
    source: EffectiveSource;
    price?: ModelPrice | null;
    provider?: ProviderId | null;
  };
  assignment: ModelAssignment | null;
  candidates: ModelCandidate[];
}

export interface CheckCounts {
  candidates: number;
  deprecations: number;
  missing: number;
  unpriced: number;
}

/** How one company's model list check went. */
export interface ProviderCheckStatus {
  ok: boolean;
  skipped?: boolean;
  modelCount?: number;
  error?: string | null;
}

export interface ProviderChecks {
  openai?: ProviderCheckStatus;
  anthropic?: ProviderCheckStatus;
}

/** How one company's price refresh went. */
export interface ProviderPriceStatus {
  ok: boolean;
  skipped?: boolean;
  updated?: number;
  unchanged?: number;
  changed?: number;
  suspicious?: number;
  rejected?: number;
  error?: string | null;
}

export interface PriceChecks {
  fetchedAt?: string;
  openai?: ProviderPriceStatus;
  anthropic?: ProviderPriceStatus;
}

export interface CheckState {
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastError: string | null;
  counts: CheckCounts | null;
  providers?: ProviderChecks | null;
  prices?: PriceChecks | null;
}

export interface GoldenSetProgress {
  total: number;
  labeled: number;
  remaining: number;
  target: number;
}

export interface ModelCurrencyOverview {
  writable: boolean;
  priceTable?: PriceTable;
  /** Older or dismissed candidates the API left out of the lists. */
  hiddenCandidates?: number;
  useCases: UseCaseOverview[];
  checkState: CheckState;
  goldenSet: GoldenSetProgress;
}

export interface CheckRunResult {
  status: string;
  notified?: boolean;
  report?: unknown;
  providers?: ProviderChecks | null;
  prices?: PriceChecks | null;
}

export interface JudgeCalibrationMetrics {
  maePerDimension: Record<string, number>;
  overallMae: number | null;
  passFailAgreement: number | null;
  runToRunMae: number | null;
}

export interface JudgeCalibrationRun {
  id: string;
  judgeModel: string;
  role: "incumbent" | "candidate";
  goldenCount: number;
  metrics: JudgeCalibrationMetrics;
  passed: boolean | null;
  checks: EvalCheck[];
  ranAt: string | null;
}

export interface GoldenDimension {
  key: string;
  label: string;
  weight: number;
  scale: { min: number; max: number };
}

/** Saved eval scenarios store the rubric as an object; older data may be plain text. */
export interface GoldenRubric {
  mustHave?: string[];
  shouldHave?: string[];
  [key: string]: unknown;
}

export interface GoldenCase {
  id: string;
  question: string;
  response: string;
  rubric: string | GoldenRubric | null;
  dimensions: GoldenDimension[];
  source: string | null;
  selectionReason: string | null;
}

export interface GoldenProgress {
  labeled: number;
  total: number;
  target: number;
}

export interface GoldenSummary extends GoldenSetProgress {
  writable: boolean;
}

export interface NextGoldenCaseResponse {
  case: GoldenCase | null;
  progress: GoldenProgress;
}

// ---- Model currency ----

/** Give up on the overview after this long so the page shows an error instead of spinning forever. */
export const OVERVIEW_TIMEOUT_MS = 30_000;

export function fetchModelCurrencyOverview(): Promise<ModelCurrencyOverview> {
  return req<ModelCurrencyOverview>(
    "GET",
    "/admin/model-currency/overview",
    undefined,
    AbortSignal.timeout(OVERVIEW_TIMEOUT_MS)
  );
}

export async function runModelCurrencyCheck(): Promise<CheckRunResult> {
  const data = await req<{ result: CheckRunResult }>("POST", "/admin/model-currency/check");
  return data.result;
}

export function promoteCandidate(id: string): Promise<{ ok: true; assignment: ModelAssignment }> {
  return req("POST", `/admin/model-currency/candidates/${encodeURIComponent(id)}/promote`);
}

export function dismissCandidate(id: string): Promise<{ ok: true }> {
  return req("POST", `/admin/model-currency/candidates/${encodeURIComponent(id)}/dismiss`);
}

/** Start the test for one candidate. The API answers 202 right away; the test runs in the background. */
export function evaluateCandidate(id: string): Promise<{ ok: true }> {
  return req("POST", `/admin/model-currency/candidates/${encodeURIComponent(id)}/evaluate`);
}

/** Start tests for every waiting candidate, one after another. `started` can be 0. */
export function evaluateAllCandidates(): Promise<{ ok: true; started: number }> {
  return req("POST", "/admin/model-currency/candidates/evaluate-all");
}

export function rollbackUseCase(
  useCase: string
): Promise<{ ok: true; effective: { model: string; source: EffectiveSource } }> {
  return req("POST", `/admin/model-currency/use-cases/${encodeURIComponent(useCase)}/rollback`);
}

export function resetBaseline(useCase: string): Promise<{ ok: true }> {
  return req(
    "POST",
    `/admin/model-currency/use-cases/${encodeURIComponent(useCase)}/baseline-reset`
  );
}

export function fetchJudgeCalibration(): Promise<{ runs: JudgeCalibrationRun[] }> {
  return req("GET", "/admin/model-currency/judge-calibration");
}

// ---- Judge golden set ----

export function fetchGoldenSummary(): Promise<GoldenSummary> {
  return req("GET", "/admin/judge-golden/summary");
}

export function fetchNextGoldenCase(afterId?: string): Promise<NextGoldenCaseResponse> {
  const qs = afterId ? `?afterId=${encodeURIComponent(afterId)}` : "";
  return req("GET", `/admin/judge-golden/next${qs}`);
}

export function saveGoldenLabels(
  id: string,
  scores: Record<string, number>,
  notes?: string
): Promise<{ ok: true; progress: GoldenProgress }> {
  const trimmed = notes?.trim();
  const body: Record<string, unknown> = { scores };
  if (trimmed) body.notes = trimmed;
  return req("PUT", `/admin/judge-golden/${encodeURIComponent(id)}/labels`, body);
}
